// ============================================================================
// testUtils.js — shared, framework-agnostic helpers used across the
// assessment components. Kept separate from UI so they're easy to unit test.
// ============================================================================

export const toInt = (v) => {
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

/**
 * Parses timestamps returned by the backend's `start`/`submit`/progress
 * endpoints (Java `LocalDateTime`, e.g. "2026-08-13T08:12:25.198465583").
 * These strings carry NO timezone/offset — they are UTC wall-clock values.
 *
 * `new Date(str)` on a date-*time* string with no zone suffix is parsed as
 * browser-LOCAL time, not UTC (a well-known JS quirk — only bare
 * "YYYY-MM-DD" date strings default to UTC). For any user whose local zone
 * differs from UTC, that silently shifts the parsed time — e.g. in IST
 * (UTC+5:30) an "endTime" of 08:42 UTC gets misread as 08:42 IST, landing
 * ~5.5 hours in the past. That's enough for a freshly-started test's
 * countdown to see `expiresAt` as already elapsed and auto-submit instantly.
 *
 * Always use this (never a bare `new Date(...)`) for start/end/expiry
 * timestamps coming from the API.
 */
export const parseServerDate = (dateStr) => {
  if (!dateStr) return null;
  const hasZoneInfo = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr);
  const d = new Date(hasZoneInfo ? dateStr : `${dateStr}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const WORD_MAP = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
  SIX: 6,
  SEVEN: 7,
  EIGHT: 8,
  NINE: 9,
  TEN: 10,
};

export const parseScale = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  const upper = s.toUpperCase();
  if (WORD_MAP[upper] != null) return WORD_MAP[upper];
  const m = s.match(/\b(10|[1-9])\b/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 10) return n;
  }
  return null;
};

export const scaleLabel = (numericValue, fallbackText = "Select") => {
  switch (numericValue) {
    case 1:
      return "Strongly Disagree";
    case 2:
      return "Disagree";
    case 3:
      return "Mostly Disagree";
    case 4:
      return "Slightly Disagree";
    case 5:
      return "Neutral";
    case 6:
      return "Slightly Agree";
    case 7:
      return "Mostly Agree";
    case 8:
      return "Agree";
    case 9:
      return "Strongly Agree";
    case 10:
      return "Completely Agree";
    default:
      return fallbackText;
  }
};

/**
 * Detect a question's UI treatment.
 * Prefers an explicit `questionType` field from the API
 * (RATING / SINGLE_CHOICE / MULTIPLE_CHOICE, case-insensitive).
 * Falls back to heuristics for APIs that don't send a type yet:
 *   - If every option parses as a 1-10 scale value -> rating
 *   - If the question flags `allowMultiple` / `multiSelect` -> multi
 *   - Otherwise -> single choice
 */
export const getQuestionType = (question) => {
  const explicit = String(question?.questionType || "").toUpperCase();
  if (explicit.includes("RATING") || explicit.includes("SCALE"))
    return "RATING";
  if (explicit.includes("MULTI")) return "MULTIPLE_CHOICE";
  if (explicit.includes("SINGLE")) return "SINGLE_CHOICE";

  const options = question?.answerOptions || [];
  const allScale =
    options.length > 0 &&
    options.every((o) => parseScale(o.answerText) != null);
  if (allScale && options.length >= 5) return "RATING";

  if (question?.allowMultiple || question?.multiSelect)
    return "MULTIPLE_CHOICE";

  return "SINGLE_CHOICE";
};

/** Flatten sections into a single ordered question list, tagged with section info. */
export const flattenQuestions = (sections = []) => {
  const flat = [];
  sections.forEach((section, sectionIndex) => {
    (section.questions || []).forEach((question, questionIndexInSection) => {
      flat.push({
        question,
        sectionIndex,
        sectionTitle: section.sectionTitle,
        questionIndexInSection,
      });
    });
  });
  return flat;
};

export const estimateSeconds = (type) => {
  if (type === "RATING") return 10;
  if (type === "MULTIPLE_CHOICE") return 20;
  return 12;
};
